/* Hinata Inbox bridge v2. 認証・通知取得・既読はiOS / Androidのネイティブ側で行います。 */
(() => {
  "use strict";
  let dispose = () => {};

  function boot() {
    const root = document.getElementById("hinata-notifications");
    if (!root || root.dataset.initialized === "true") return;
    dispose();
    root.dataset.initialized = "true";
    const $ = id => root.querySelector(`#${id}`);
    const bridgeVersion = 2;
    const preview = root.dataset.designMode === "true" || window.Shopify?.designMode === true;
    const bundled = window.location.protocol === "file:" || window.location.hostname === "appassets.androidplatform.net";
    const androidBridge = window.HinataNotificationsAndroid;
    const bridge = androidBridge && typeof androidBridge.postMessage === "function"
      ? { postMessage: message => androidBridge.postMessage(JSON.stringify(message)) }
      : window.webkit?.messageHandlers?.hinataNotifications;
    const inApp = !!bridge && typeof bridge.postMessage === "function" && !preview;
    const dateFormat = new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit"
    });
    let state = { notifications: [], pendingReadIDs: [], hasLoaded: false };
    let detail = null;
    let savedScroll = 0;
    let selectedID = null;
    let listSignature = null;
    let visible = false;
    let appearanceID = "";
    let measuredAppearanceID = "";
    let lastTap = { id: "", time: 0 };
    let eventTimer = null;
    const eventQueue = [];

    // 発行APIの準備だけ短時間待ちます。同意を代行・変更したり、SDKへ直送しません。
    function flushEvents() {
      if (eventTimer !== null) window.clearTimeout(eventTimer);
      eventTimer = null;
      const now = Date.now();
      while (eventQueue.length && now - eventQueue[0].time > 10000) eventQueue.shift();
      if (!eventQueue.length) return;
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish !== "function") {
        eventTimer = window.setTimeout(flushEvents, 250);
        return;
      }
      // 一度発行したイベントは再送しません。計測失敗で画面操作を止めません。
      while (eventQueue.length) {
        const event = eventQueue.shift();
        try {
          const result = analytics.publish(event.name, event.data);
          if (result && typeof result.catch === "function") result.catch(() => {});
        } catch (_) { /* 計測はbest effort */ }
      }
    }

    function track(name, data = {}) {
      if (!inApp || preview || bundled || !visible) return;
      if (eventQueue.length >= 20) eventQueue.shift();
      eventQueue.push({ name, data: { screen_name: "notifications", ...data }, time: Date.now() });
      flushEvents();
    }

    function trackVisibleInbox() {
      if (!visible || !state.hasLoaded || detail || !appearanceID || measuredAppearanceID === appearanceID) return;
      measuredAppearanceID = appearanceID;
      const data = { item_count: state.notifications.length };
      if (Number.isInteger(state.unreadCount)) data.unread_count = state.unreadCount;
      track("hinata:inbox_viewed", data);
    }

    function setVisibility(isVisible, id) {
      visible = isVisible === true;
      if (typeof id === "string") appearanceID = id;
      trackVisibleInbox();
    }

    function post(action, extra = {}) {
      if (preview) {
        if (action === "open" || action === "openLink") {
          const item = state.notifications.find(item => item.id === extra.notificationId);
          if (item) {
            if (action === "openLink") { showMessage("サンプルのため関連ページには移動しません。"); return; }
            showDetail(item, "preview");
            render({ ...state, notifications: state.notifications.map(row => row.id === item.id ? { ...row, isRead: true } : row),
              unreadCount: state.notifications.filter(row => row.id !== item.id && !row.isRead).length });
          }
        }
        return;
      }
      if (!inApp) return;
      try { bridge.postMessage({ action, ...extra }); }
      catch (_) { showMessage("アプリとの接続を確認できません。画面を開き直してください。"); }
    }

    function text(tag, className, value) {
      const node = document.createElement(tag);
      node.className = className;
      node.textContent = typeof value === "string" ? value : "";
      return node;
    }

    function formatDate(raw) {
      const date = new Date(raw);
      return raw && Number.isFinite(date.getTime()) ? dateFormat.format(date) : "";
    }

    function safeHTTPS(raw) {
      if (typeof raw !== "string" || !raw) return null;
      try {
        const url = new URL(raw);
        return url.protocol === "https:" && !url.username && !url.password ? url.href : null;
      } catch (_) { return null; }
    }

    function tap(item, action) {
      const key = `${action}:${item.id}`;
      if (lastTap.id === key && Date.now() - lastTap.time < 700) return;
      lastTap = { id: key, time: Date.now() };
      selectedID = item.id;
      track(action === "openLink" ? "hinata:notification_link_clicked" : "hinata:notification_clicked", {
        notification_id: item.id,
        was_unread: item.isRead ? 0 : 1,
        destination: safeHTTPS(item.linkUrl) ? "page" : "detail"
      });
      post(action, { notificationId: item.id });
    }

    function makeRow(item) {
      const li = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = item.isRead ? "hn-notice" : "hn-notice unread";
      button.dataset.id = item.id;
      button.setAttribute("aria-label", `${item.isRead ? "既読" : "未読"}、${item.title}、${formatDate(item.publishedAt)}`);
      const main = text("span", "hn-notice-main", "");
      const meta = text("span", "hn-meta", "");
      meta.append(text("span", "hn-date", formatDate(item.publishedAt)));
      if (!item.isRead) meta.append(text("span", "hn-unread-label", "未読"));
      main.append(meta, text("span", "hn-title", item.title), text("span", "hn-body", item.body));
      button.append(main);
      const imageURL = safeHTTPS(item.imageUrl);
      if (imageURL) {
        const img = document.createElement("img");
        img.className = "hn-image";
        img.alt = "";
        img.loading = "lazy";
        img.referrerPolicy = "no-referrer";
        img.src = imageURL;
        img.addEventListener("error", () => img.remove(), { once: true });
        button.append(img);
      }
      const arrow = text("span", "hn-chevron", "›");
      arrow.setAttribute("aria-hidden", "true");
      button.append(arrow);
      button.addEventListener("click", () => tap(item, "open"));
      li.append(button);
      return li;
    }

    function render(nextState) {
      // 通常ブラウザでAPIを呼び出しても個別通知のUIを有効化しません。
      if (!inApp && !preview) return;
      const y = window.scrollY;
      const focusedID = document.activeElement?.dataset?.id;
      state = { ...nextState, notifications: Array.isArray(nextState.notifications) ? nextState.notifications : [] };
      const count = state.unreadCount;
      $("unread").textContent = Number.isInteger(count) ? (count > 0 ? `未読 ${count}件` : "すべて確認済み") : "確認中";
      $("unread").classList.toggle("zero", count === 0);
      $("refresh").disabled = !!state.isRefreshing;
      $("refresh").textContent = state.isRefreshing ? "更新中" : "更新";
      const list = state.notifications;
      const pending = new Set(state.pendingReadIDs || []);
      $("pending").hidden = pending.size === 0 || !state.error;
      $("pending").textContent = "通信が戻り次第、確認済みの状態を保存します。";
      $("loading").hidden = !!state.hasLoaded || !!state.error || list.length > 0;
      $("empty").hidden = !state.hasLoaded || list.length > 0 || !!state.error;
      $("error").hidden = !state.error;
      $("errorText").textContent = state.error || "";
      const signature = JSON.stringify(list);
      if (listSignature !== signature) {
        $("notices").replaceChildren(...list.map(makeRow));
        listSignature = signature;
      }
      $("more").hidden = !state.hasMore;
      $("more").disabled = !!state.isLoadingMore || !!state.isRefreshing;
      $("more").textContent = state.isLoadingMore ? "読み込んでいます…" : "以前のお知らせを表示";
      $("end").hidden = !state.hasLoaded || state.hasMore || list.length === 0;
      if (detail) detail = list.find(item => item.id === detail.id) || detail;
      if (focusedID) Array.from($("notices").querySelectorAll("button")).find(node => node.dataset.id === focusedID)?.focus({ preventScroll: true });
      if (!detail) window.scrollTo(0, y);
      trackVisibleInbox();
    }

    function showDetail(item, source = "inbox") {
      if (!inApp && !preview) return;
      if (!detail) savedScroll = window.scrollY;
      detail = item;
      $("detailDate").textContent = formatDate(item.publishedAt);
      $("detailTitle").textContent = item.title;
      $("detailBody").textContent = item.body;
      const url = safeHTTPS(item.imageUrl);
      $("detailImage").hidden = !url;
      if (url) {
        $("detailImage").src = url;
        $("detailImage").referrerPolicy = "no-referrer";
        $("detailImage").onerror = () => { $("detailImage").hidden = true; };
      } else { $("detailImage").removeAttribute("src"); }
      $("detailLink").hidden = !safeHTTPS(item.linkUrl);
      $("inbox").hidden = true;
      $("detail").hidden = false;
      window.scrollTo(0, 0);
      $("detailTitle").focus({ preventScroll: true });
      track("hinata:notification_detail_viewed", { notification_id: item.id, entry_point: source });
    }

    function closeDetail() {
      detail = null;
      $("detail").hidden = true;
      $("inbox").hidden = false;
      window.requestAnimationFrame(() => {
        window.scrollTo(0, savedScroll);
        Array.from($("notices").querySelectorAll("button")).find(node => node.dataset.id === selectedID)?.focus({ preventScroll: true });
      });
      post("closeDetail");
      trackVisibleInbox();
    }

    function showMessage(message) {
      if (detail) closeDetail();
      $("errorText").textContent = message;
      $("error").hidden = false;
    }

    $("refresh").addEventListener("click", () => post("refresh"));
    $("retry").addEventListener("click", () => post("refresh"));
    $("more").addEventListener("click", () => post("loadMore"));
    $("closeDetail").addEventListener("click", closeDetail);
    $("detailLink").addEventListener("click", () => { if (detail) tap(detail, "openLink"); });
    window.HinataInbox = Object.freeze({ bridgeVersion, render, showDetail, showMessage, setVisibility });
    dispose = () => { if (eventTimer !== null) window.clearTimeout(eventTimer); eventQueue.length = 0; };

    if (preview) {
      $("previewNote").hidden = false;
      const date = new Date().toISOString();
      render({ hasLoaded: true, hasMore: false, unreadCount: 2, pendingReadIDs: [], notifications: [
        { id: "sample-1", title: "暮らしを整える、新しいアイテム", body: "お気に入りの空間に、心地よい変化を。\nこちらはテーマ編集用のサンプルです。", publishedAt: date, isRead: false, linkUrl: "https://hinatalife.com/collections/all" },
        { id: "sample-2", title: "ひなたライフからのお知らせ", body: "タップするとお知らせの本文を確認できます。", publishedAt: date, isRead: false },
        { id: "sample-3", title: "いつもご利用ありがとうございます", body: "確認したお知らせは、このように表示されます。", publishedAt: date, isRead: true }
      ] });
    } else if (inApp) {
      $("fallbackNote").hidden = !bundled;
      post("ready", { bridgeVersion });
    } else {
      $("loading").hidden = true;
      $("refresh").hidden = true;
      $("unread").hidden = true;
      $("browserOnly").hidden = false;
    }
  }

  boot();
  // テーマエディタでセクション設定を変更するとHTMLが差し替わります。
  document.addEventListener("shopify:section:load", boot);
})();
